// TransactionLedger.jsx - Complete Ledger with Full User Profile and All Data
import React, { useState, useEffect, useRef } from 'react';
import Sidebar from '../../components/sidebar';
import Header from '../../components/header';
import { apiCall } from '../../utils/api';
import toast, { Toaster } from 'react-hot-toast';
import {
    FaUser, FaPhone, FaEnvelope, FaCalendarAlt, FaIdCard,
    FaMapMarkerAlt, FaUserTag, FaVenusMars, FaAddressCard,
    FaCheckCircle, FaClock, FaInfoCircle, FaFilePdf, FaFileExcel
} from "react-icons/fa";
import { useLocation } from 'react-router-dom';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

// Properly import autoTable
import autoTable from 'jspdf-autotable';

function TransactionLedger() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState('dashboard');
    const [transactions, setTransactions] = useState([]);
    const [filteredTransactions, setFilteredTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [userProfile, setUserProfile] = useState(null);
    const tableRef = useRef(null);

    // Filter states
    const [filters, setFilters] = useState({
        dateRange: {
            start: '',
            end: ''
        }
    });

    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const user_id = params.get("user_id");

    // Company Details
    const companyDetails = {
        name: "PayWon Private Limited",
        cin: "87246985795",
        gst: "87698479844",
        contact: "+917002055429",
        email: "gdhgduj@gmil.com",
        address: "Dalgaon, Darrang, Assam, PIN 784116"
    };

    // Helper function to format profile image
    const getProfileImage = (imageData) => {
        if (!imageData) return null;

        if (imageData.startsWith('http://') || imageData.startsWith('https://')) {
            return imageData;
        }

        if (imageData.startsWith('/9j/') || imageData.startsWith('iVBOR')) {
            return `data:image/jpeg;base64,${imageData}`;
        }

        if (imageData.startsWith('/')) {
            return `${process.env.REACT_APP_API_URL || ''}${imageData}`;
        }

        return imageData;
    };

    // Fetch Transactions with filters
    const fetchTransactions = async () => {
        setLoading(true);
        try {
            const params = {
                user_id,
                startDate: filters.dateRange.start,
                endDate: filters.dateRange.end,
            };

            const result = await apiCall("ledger", params, true);

            if (result.status === true) {
                setTransactions(result.transactions || []);
                setFilteredTransactions(result.transactions || []);
                setUserProfile(result.profile || null);
            } else {
                toast.error(result.message || "Failed to fetch transactions");
                setTransactions([]);
                setFilteredTransactions([]);
            }
        } catch (error) {
            toast.error("Network error. Please try again.");
            setTransactions([]);
            setFilteredTransactions([]);
        } finally {
            setLoading(false);
        }
    };

    // Initial fetch
    useEffect(() => {
        fetchTransactions();
    }, [user_id]);

    // Handle filter changes
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            dateRange: {
                ...prev.dateRange,
                [name]: value
            }
        }));
    };

    // Apply filters
    const applyFilters = () => {
        fetchTransactions();
    };

    // Clear filters
    const clearFilters = () => {
        setFilters({
            dateRange: {
                start: '',
                end: ''
            }
        });
        setTimeout(() => {
            fetchTransactions();
        }, 100);
    };

    // Get status color
    const getStatusColor = (status) => {
        const colors = {
            'Success': 'bg-green-100 text-green-800',
            'Failed': 'bg-red-100 text-red-800',
            'Pending': 'bg-yellow-100 text-yellow-800',
            'Initiated': 'bg-blue-100 text-blue-800'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    // Format currency
    const formatCurrency = (amount) => {
        return `₹${parseFloat(amount || 0).toFixed(2)}`;
    };

    // Calculate totals
    const calculateTotals = () => {
        const totalCredit = filteredTransactions.reduce((sum, t) => sum + parseFloat(t.credit || 0), 0);
        const totalDebit = filteredTransactions.reduce((sum, t) => sum + parseFloat(t.debit || 0), 0);
        const openingBalance = filteredTransactions.length > 0 ? parseFloat(filteredTransactions[filteredTransactions.length - 1]?.previousBalance || 0) : 0;
        const closingBalance = filteredTransactions.length > 0 ? parseFloat(filteredTransactions[0]?.balanceAfter || 0) : 0;
        return { totalCredit, totalDebit, openingBalance, closingBalance };
    };

    // Download as PDF
    // Download as PDF
    const downloadPDF = () => {
        if (filteredTransactions.length === 0) {
            toast.error("No transactions to download");
            return;
        }

        const doc = new jsPDF('landscape', 'mm', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();

        // Company Header
        doc.setFontSize(18);
        doc.setTextColor(0, 0, 128);
        doc.text(companyDetails.name, pageWidth / 2, 15, { align: 'center' });

        doc.setFontSize(9);
        doc.setTextColor(80, 80, 80);
        doc.text(`CIN: ${companyDetails.cin} | GST: ${companyDetails.gst}`, pageWidth / 2, 22, { align: 'center' });
        doc.text(`Contact: ${companyDetails.contact} | Email: ${companyDetails.email}`, pageWidth / 2, 28, { align: 'center' });
        doc.text(`Address: ${companyDetails.address}`, pageWidth / 2, 34, { align: 'center' });

        // Horizontal Line
        doc.setDrawColor(0, 0, 128);
        doc.line(10, 37, pageWidth - 10, 37);

        // Title
        doc.setFontSize(14);
        doc.setTextColor(0, 0, 0);
        doc.text('BANK STATEMENT', pageWidth / 2, 44, { align: 'center' });

        // Date Range
        doc.setFontSize(9);
        doc.setTextColor(80, 80, 80);
        const dateRangeText = filters.dateRange.start && filters.dateRange.end
            ? `Period: ${filters.dateRange.start} to ${filters.dateRange.end}`
            : 'Period: All Transactions';
        doc.text(dateRangeText, pageWidth / 2, 50, { align: 'center' });

        // Customer Details with Full KYC Information
        if (userProfile) {
            let startY = 58;

            // Personal Information Section
            doc.setFontSize(11);
            doc.setTextColor(0, 0, 128);
            doc.text('CUSTOMER DETAILS', 14, startY);

            doc.setFontSize(9);
            doc.setTextColor(0, 0, 0);
            startY += 6;

            // Row 1: Name and Mobile
            doc.text(`Name: ${userProfile.name || 'N/A'}`, 14, startY);
            doc.text(`Mobile: ${userProfile.mobile || 'N/A'}`, 80, startY);
            doc.text(`Email: ${userProfile.email || 'N/A'}`, 140, startY);

            startY += 6;

            // Row 2: User ID and IP
            doc.text(`User ID: ${userProfile.user_id || 'N/A'}`, 14, startY);
            doc.text(`IP Address: ${userProfile.ip || 'N/A'}`, 80, startY);
            doc.text(`KYC Status: ${userProfile.kyc_id === '3' ? '✅ Verified' : '⏳ Pending'}`, 140, startY);

            startY += 6;

            // Row 3: Status and Date
            doc.text(`Status: ${userProfile.status === '1' ? 'Active' : 'Inactive'}`, 14, startY);
            doc.text(`Last Updated: ${userProfile.date || 'N/A'}`, 80, startY);
            if (userProfile.time) {
                doc.text(`Time: ${userProfile.time}`, 140, startY);
            }

            startY += 8;

            // Aadhaar Details Section
            if (userProfile.aadhaar) {
                doc.setFontSize(10);
                doc.setTextColor(0, 0, 128);
                doc.text('AADHAAR DETAILS', 14, startY);

                doc.setFontSize(9);
                doc.setTextColor(0, 0, 0);
                startY += 6;

                // Aadhaar Row 1
                doc.text(`Aadhaar Number: ${userProfile.aadhaar || 'N/A'}`, 14, startY);
                doc.text(`Name: ${userProfile.aadhaar_name || 'N/A'}`, 80, startY);
                doc.text(`Father: ${userProfile.aadhaar_father || 'N/A'}`, 140, startY);

                startY += 6;

                // Aadhaar Row 2
                doc.text(`DOB: ${userProfile.aadhaar_dob || 'N/A'}`, 14, startY);
                doc.text(`Gender: ${userProfile.aadhaar_gender || 'N/A'}`, 80, startY);
                doc.text(`State: ${userProfile.aadhaar_state || 'N/A'}`, 140, startY);

                startY += 6;

                // Aadhaar Row 3
                doc.text(`District: ${userProfile.aadhaar_district || 'N/A'}`, 14, startY);
                doc.text(`PIN: ${userProfile.aadhaar_pin || 'N/A'}`, 80, startY);

                startY += 8;
            }

            // PAN Details Section
            if (userProfile.pan) {
                doc.setFontSize(10);
                doc.setTextColor(0, 0, 128);
                doc.text('PAN DETAILS', 14, startY);

                doc.setFontSize(9);
                doc.setTextColor(0, 0, 0);
                startY += 6;

                doc.text(`PAN Number: ${userProfile.pan || 'N/A'}`, 14, startY);
                doc.text(`PAN Name: ${userProfile.pan_name || 'N/A'}`, 80, startY);

                startY += 6;

                if (userProfile.pan_dob && userProfile.pan_dob !== '0000-00-00') {
                    doc.text(`PAN DOB: ${userProfile.pan_dob}`, 14, startY);
                }
                if (userProfile.pan_father) {
                    doc.text(`PAN Father: ${userProfile.pan_father}`, 80, startY);
                }

                startY += 8;
            }

            // Address Section
            if (userProfile.address) {
                doc.setFontSize(10);
                doc.setTextColor(0, 0, 128);
                doc.text('ADDRESS', 14, startY);

                doc.setFontSize(9);
                doc.setTextColor(0, 0, 0);
                startY += 6;
                doc.text(`${userProfile.address}`, 14, startY);
                startY += 8;
            }

            // Token if exists
            if (userProfile.token) {
                doc.setFontSize(8);
                doc.setTextColor(80, 80, 80);
                doc.text(`Token: ${userProfile.token}`, 14, startY);
                startY += 6;
            }

            // Add a separator line before transactions
            doc.setDrawColor(200, 200, 200);
            doc.line(10, startY + 2, pageWidth - 10, startY + 2);
            startY += 6;
        }

        // Prepare table data
        const tableRows = filteredTransactions.map(t => [
            t.orderTime || '',
            t.orderId || '',
            t.referenceOrderId || '',
            t.orderType || '',
            t.consumerName || '',
            t.txn || '',
            formatCurrency(t.amount),
            formatCurrency(t.previousBalance),
            parseFloat(t.credit || 0) > 0 ? formatCurrency(t.credit) : '-',
            parseFloat(t.debit || 0) > 0 ? formatCurrency(t.debit) : '-',
            formatCurrency(t.balanceAfter),
            t.statusId || '',
            t.operatorName || '',
        ]);

        // Calculate starting Y position for table
        let tableStartY = 58;
        if (userProfile) {
            // Calculate how many rows of customer details we added
            let rows = 4; // Personal info rows
            if (userProfile.aadhaar) rows += 4; // Aadhaar rows
            if (userProfile.pan) rows += 3; // PAN rows
            if (userProfile.address) rows += 2; // Address rows
            if (userProfile.token) rows += 1; // Token row
            tableStartY = 58 + (rows * 6) + 10;
        }

        // Add table using autoTable
        doc.autoTable({
            startY: tableStartY,
            head: [[
                'Date', 'Order ID', 'Ref. ID', 'Type', 'Consumer', 'TXN',
                'Amount', 'Opening', 'Credit', 'Debit', 'Closing', 'Status', 'Operator'
            ]],
            body: tableRows,
            theme: 'grid',
            styles: {
                fontSize: 7,
                cellPadding: 2,
            },
            headStyles: {
                fillColor: [0, 0, 128],
                textColor: [255, 255, 255],
                fontSize: 7,
                fontStyle: 'bold',
            },
            columnStyles: {
                0: { cellWidth: 22 },
                1: { cellWidth: 20 },
                2: { cellWidth: 20 },
                3: { cellWidth: 12 },
                4: { cellWidth: 20 },
                5: { cellWidth: 18 },
                6: { cellWidth: 18 },
                7: { cellWidth: 18 },
                8: { cellWidth: 18 },
                9: { cellWidth: 18 },
                10: { cellWidth: 18 },
                11: { cellWidth: 16 },
                12: { cellWidth: 18 },
            },
            didDrawPage: function (data) {
                // Footer
                const pageNumber = doc.internal.getCurrentPageInfo().pageNumber;
                const totalPages = doc.internal.getNumberOfPages();
                doc.setFontSize(7);
                doc.setTextColor(128, 128, 128);
                doc.text(
                    `Page ${pageNumber} of ${totalPages}`,
                    pageWidth - 20,
                    pageHeight - 8
                );
                doc.text(
                    `Generated on: ${new Date().toLocaleString()}`,
                    14,
                    pageHeight - 8
                );
            }
        });

        // Summary Section
        const finalY = doc.lastAutoTable.finalY + 10;
        const totals = calculateTotals();

        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);

        // Summary Box
        doc.setDrawColor(0, 0, 128);
        doc.rect(14, finalY, pageWidth - 28, 40);

        doc.setFontSize(9);
        doc.text('SUMMARY', 20, finalY + 10);

        doc.setFontSize(8);
        const summaryY = finalY + 20;
        doc.text(`Opening Balance: ${formatCurrency(totals.openingBalance)}`, 20, summaryY);
        doc.text(`Total Credit: ${formatCurrency(totals.totalCredit)}`, 70, summaryY);
        doc.text(`Total Debit: ${formatCurrency(totals.totalDebit)}`, 120, summaryY);
        doc.text(`Closing Balance: ${formatCurrency(totals.closingBalance)}`, 170, summaryY);

        // Footer
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text('This is a system generated statement.', pageWidth / 2, pageHeight - 20, { align: 'center' });

        doc.save(`bank_statement_${userProfile?.name || 'customer'}_${new Date().toISOString().split('T')[0]}.pdf`);
        toast.success("PDF downloaded successfully!");
    };

    // Download as Excel
    const downloadExcel = () => {
        if (filteredTransactions.length === 0) {
            toast.error("No transactions to download");
            return;
        }

        const totals = calculateTotals();

        // Prepare data for Excel
        const excelData = [
            // Company Header
            ['Company:', companyDetails.name],
            ['CIN:', companyDetails.cin],
            ['GST:', companyDetails.gst],
            ['Contact:', companyDetails.contact],
            ['Email:', companyDetails.email],
            ['Address:', companyDetails.address],
            [],
            ['BANK STATEMENT'],
            [],
        ];

        // Customer Details
        if (userProfile) {
            excelData.push(['Customer Details']);
            excelData.push(['Name:', userProfile.name]);
            excelData.push(['Mobile:', userProfile.mobile]);
            excelData.push(['Email:', userProfile.email]);
            excelData.push(['User ID:', userProfile.user_id]);
            excelData.push(['KYC Status:', userProfile.kyc_id === '3' ? 'Verified' : 'Pending']);
            excelData.push([]);
        }

        // Date Range
        const dateRangeText = filters.dateRange.start && filters.dateRange.end
            ? `Period: ${filters.dateRange.start} to ${filters.dateRange.end}`
            : 'Period: All Transactions';
        excelData.push([dateRangeText]);
        excelData.push([]);

        // Transaction Headers
        excelData.push([
            'Date', 'Order ID', 'Ref. ID', 'Type', 'Consumer', 'TXN',
            'Amount', 'Opening Balance', 'Credit', 'Debit', 'Closing Balance',
            'Status', 'Operator', 'Provider'
        ]);

        // Transaction Data
        filteredTransactions.forEach(t => {
            excelData.push([
                t.orderTime || '',
                t.orderId || '',
                t.referenceOrderId || '',
                t.orderType || '',
                t.consumerName || '',
                t.txn || '',
                parseFloat(t.amount || 0),
                parseFloat(t.previousBalance || 0),
                parseFloat(t.credit || 0),
                parseFloat(t.debit || 0),
                parseFloat(t.balanceAfter || 0),
                t.statusId || '',
                t.operatorName || '',
                t.providerName || '',
            ]);
        });

        // Summary Section
        excelData.push([]);
        excelData.push(['SUMMARY']);
        excelData.push(['Opening Balance', formatCurrency(totals.openingBalance)]);
        excelData.push(['Total Credit', formatCurrency(totals.totalCredit)]);
        excelData.push(['Total Debit', formatCurrency(totals.totalDebit)]);
        excelData.push(['Closing Balance', formatCurrency(totals.closingBalance)]);
        excelData.push([]);
        excelData.push(['Total Transactions', filteredTransactions.length]);

        // Create workbook
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet(excelData);

        // Set column widths
        ws['!cols'] = [
            { wch: 20 }, // Date
            { wch: 20 }, // Order ID
            { wch: 20 }, // Ref. ID
            { wch: 15 }, // Type
            { wch: 25 }, // Consumer
            { wch: 18 }, // TXN
            { wch: 15 }, // Amount
            { wch: 18 }, // Opening Balance
            { wch: 15 }, // Credit
            { wch: 15 }, // Debit
            { wch: 18 }, // Closing Balance
            { wch: 15 }, // Status
            { wch: 20 }, // Operator
            { wch: 20 }, // Provider
        ];

        XLSX.utils.book_append_sheet(wb, ws, 'Statement');
        XLSX.writeFile(wb, `bank_statement_${userProfile?.name || 'customer'}_${new Date().toISOString().split('T')[0]}.xlsx`);
        toast.success("Excel downloaded successfully!");
    };

    return (
        <div className="bg-gray-50 font-sans antialiased min-h-screen">
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            <Sidebar
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                isOpen={sidebarOpen}
                setIsOpen={setSidebarOpen}
            />

            <div className="main-content lg:ml-[72px] transition-all duration-200">
                <Header
                    currentPage={currentPage}
                    sidebarOpen={sidebarOpen}
                    setSidebarOpen={setSidebarOpen}
                />

                <div className="p-4 sm:p-6 lg:p-8">
                    {/* Page Title */}
                    <div className="mb-6">
                        <h1 className="text-2xl font-bold text-gray-900">Transaction Ledger</h1>
                        <p className="text-gray-600 text-sm">Complete transaction history with running balance</p>
                    </div>

                    {/* Date Filters */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
                        <div className="flex flex-col sm:flex-row items-end gap-4">
                            <div className="flex-1">
                                <label className="block text-xs font-medium text-gray-700 mb-1">Start Date</label>
                                <div className="relative">
                                    <FaCalendarAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                    <input
                                        type="date"
                                        name="start"
                                        value={filters.dateRange.start}
                                        onChange={handleFilterChange}
                                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                            </div>
                            <div className="flex-1">
                                <label className="block text-xs font-medium text-gray-700 mb-1">End Date</label>
                                <div className="relative">
                                    <FaCalendarAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                    <input
                                        type="date"
                                        name="end"
                                        value={filters.dateRange.end}
                                        onChange={handleFilterChange}
                                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={applyFilters}
                                    className="px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors duration-200"
                                >
                                    Apply Filters
                                </button>
                                <button
                                    onClick={clearFilters}
                                    className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors duration-200"
                                >
                                    Clear
                                </button>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={downloadPDF}
                                    disabled={filteredTransactions.length === 0}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center gap-2 ${filteredTransactions.length === 0
                                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                            : 'bg-red-600 text-white hover:bg-red-700'
                                        }`}
                                >
                                    <FaFilePdf />
                                    PDF
                                </button>
                                <button
                                    onClick={downloadExcel}
                                    disabled={filteredTransactions.length === 0}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center gap-2 ${filteredTransactions.length === 0
                                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                            : 'bg-green-600 text-white hover:bg-green-700'
                                        }`}
                                >
                                    <FaFileExcel />
                                    Excel
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Full User Profile Card */}
                    {userProfile && (
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 overflow-hidden">
                            {/* Profile Header */}
                            <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4">
                                <div className="flex items-center gap-4">
                                    <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center border-2 border-white overflow-hidden">
                                        {userProfile.profile_image ? (
                                            <img
                                                src={getProfileImage(userProfile.profile_image)}
                                                alt={userProfile.name}
                                                className="w-16 h-16 rounded-full object-cover"
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.style.display = 'none';
                                                    e.target.parentElement.innerHTML = `<svg class="w-8 h-8 text-blue-600" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd"/></svg>`;
                                                }}
                                            />
                                        ) : (
                                            <FaUser className="text-blue-600 text-2xl" />
                                        )}
                                    </div>
                                    <div className="text-white">
                                        <h2 className="text-xl font-bold">{userProfile.name}</h2>
                                    </div>
                                </div>
                            </div>

                            {/* Profile Details Grid */}
                            <div className="p-6">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {/* Personal Information */}
                                    <div className="bg-gray-50 rounded-lg p-4">
                                        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                            <FaUserTag className="text-blue-500" />
                                            Personal Information
                                        </h3>
                                        <div className="space-y-2 text-sm">
                                            <div className="flex justify-between">
                                                <span className="text-gray-500">Name</span>
                                                <span className="font-medium">{userProfile.name}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-500">Mobile</span>
                                                <span className="font-medium">{userProfile.mobile}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-500">Email</span>
                                                <span className="font-medium">{userProfile.email}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-500">User ID</span>
                                                <span className="font-medium">{userProfile.user_id}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-500">IP Address</span>
                                                <span className="font-medium">{userProfile.ip}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-500">KYC Status</span>
                                                <span className={`font-medium ${userProfile.kyc_id === '3' ? 'text-green-600' : 'text-yellow-600'}`}>
                                                    {userProfile.kyc_id === '3' ? '✅ Verified' : '⏳ Pending'}
                                                </span>
                                            </div>
                                            {userProfile.token && (
                                                <div className="flex justify-between">
                                                    <span className="text-gray-500">Token</span>
                                                    <span className="font-medium text-xs truncate max-w-[120px]">{userProfile.token}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Aadhaar Details */}
                                    <div className="bg-gray-50 rounded-lg p-4">
                                        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                            <FaAddressCard className="text-blue-500" />
                                            Aadhaar Details
                                        </h3>
                                        {userProfile.aadhaar ? (
                                            <div className="space-y-2 text-sm">
                                                <div className="flex justify-between">
                                                    <span className="text-gray-500">Aadhaar</span>
                                                    <span className="font-medium">{userProfile.aadhaar}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-500">Name</span>
                                                    <span className="font-medium">{userProfile.aadhaar_name}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-500">Father</span>
                                                    <span className="font-medium">{userProfile.aadhaar_father}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-500">DOB</span>
                                                    <span className="font-medium">{userProfile.aadhaar_dob}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-500">Gender</span>
                                                    <span className="font-medium flex items-center gap-1">
                                                        <FaVenusMars />
                                                        {userProfile.aadhaar_gender}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-500">State</span>
                                                    <span className="font-medium">{userProfile.aadhaar_state}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-500">District</span>
                                                    <span className="font-medium">{userProfile.aadhaar_district}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-500">PIN</span>
                                                    <span className="font-medium">{userProfile.aadhaar_pin}</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <p className="text-gray-500 text-sm">No Aadhaar details available</p>
                                        )}
                                    </div>

                                    {/* Address & Other Details */}
                                    <div className="bg-gray-50 rounded-lg p-4">
                                        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                            <FaMapMarkerAlt className="text-blue-500" />
                                            Address & Other
                                        </h3>
                                        {userProfile.address && (
                                            <div className="space-y-2 text-sm">
                                                <div>
                                                    <span className="text-gray-500 block">Address</span>
                                                    <span className="font-medium">{userProfile.address}</span>
                                                </div>
                                            </div>
                                        )}
                                        <div className="mt-3 space-y-2 text-sm">
                                            {userProfile.pan && (
                                                <div className="flex justify-between">
                                                    <span className="text-gray-500">PAN</span>
                                                    <span className="font-medium">{userProfile.pan}</span>
                                                </div>
                                            )}
                                            {userProfile.pan_name && (
                                                <div className="flex justify-between">
                                                    <span className="text-gray-500">PAN Name</span>
                                                    <span className="font-medium">{userProfile.pan_name}</span>
                                                </div>
                                            )}
                                            {userProfile.pan_dob && userProfile.pan_dob !== '0000-00-00' && (
                                                <div className="flex justify-between">
                                                    <span className="text-gray-500">PAN DOB</span>
                                                    <span className="font-medium">{userProfile.pan_dob}</span>
                                                </div>
                                            )}
                                            {userProfile.pan_father && (
                                                <div className="flex justify-between">
                                                    <span className="text-gray-500">PAN Father</span>
                                                    <span className="font-medium">{userProfile.pan_father}</span>
                                                </div>
                                            )}
                                            <div className="flex justify-between">
                                                <span className="text-gray-500">Status</span>
                                                <span className={`font-medium ${userProfile.status === '1' ? 'text-green-600' : 'text-red-600'}`}>
                                                    {userProfile.status === '1' ? 'Active' : 'Inactive'}
                                                </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-500">Last Updated</span>
                                                <span className="font-medium">{userProfile.date}</span>
                                            </div>
                                            {userProfile.time && (
                                                <div className="flex justify-between">
                                                    <span className="text-gray-500">Time</span>
                                                    <span className="font-medium">{userProfile.time}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Balance Summary Card */}
                    {filteredTransactions.length > 0 && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                                <p className="text-xs text-gray-500 uppercase tracking-wider">Opening Balance</p>
                                <p className="text-xl font-bold text-gray-900">
                                    {formatCurrency(filteredTransactions[filteredTransactions.length - 1]?.previousBalance)}
                                </p>
                            </div>
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                                <p className="text-xs text-gray-500 uppercase tracking-wider">Total Credit</p>
                                <p className="text-xl font-bold text-green-600">
                                    {formatCurrency(filteredTransactions.reduce((sum, t) => sum + parseFloat(t.credit || 0), 0))}
                                </p>
                            </div>
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                                <p className="text-xs text-gray-500 uppercase tracking-wider">Total Debit</p>
                                <p className="text-xl font-bold text-red-600">
                                    {formatCurrency(filteredTransactions.reduce((sum, t) => sum + parseFloat(t.debit || 0), 0))}
                                </p>
                            </div>
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                                <p className="text-xs text-gray-500 uppercase tracking-wider">Closing Balance</p>
                                <p className="text-xl font-bold text-gray-900">
                                    {formatCurrency(filteredTransactions[0]?.balanceAfter)}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Transaction Table - Complete Ledger with All Data */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" ref={tableRef}>
                        {loading ? (
                            <div className="flex justify-center items-center py-12">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Date & Time
                                            </th>
                                            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Order ID
                                            </th>
                                            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Client Order ID
                                            </th>
                                            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Ref. ID
                                            </th>
                                            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Type
                                            </th>
                                            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Name
                                            </th>
                                            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Consumer No.
                                            </th>
                                            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                TXN
                                            </th>
                                            <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Amount
                                            </th>
                                            <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Opening Balance
                                            </th>
                                            <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Credit
                                            </th>
                                            <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Debit
                                            </th>
                                            <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Closing Balance
                                            </th>
                                            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Status
                                            </th>
                                            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Operator
                                            </th>
                                            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Provider
                                            </th>
                                            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Refund
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {filteredTransactions.length > 0 ? (
                                            filteredTransactions.map((transaction, index) => {
                                                const isCredit = parseFloat(transaction.credit || 0) > 0;
                                                const isDebit = parseFloat(transaction.debit || 0) > 0;
                                                const balance = parseFloat(transaction.balanceAfter || 0);
                                                const previousBalance = parseFloat(transaction.previousBalance || 0);

                                                return (
                                                    <tr key={transaction.id} className="hover:bg-gray-50 transition-colors duration-150">
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                                            <div className="font-medium">{transaction.orderTime}</div>
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                                                            {transaction.orderId}
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                                                            {transaction.clientOrderId}
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                                                            {transaction.referenceOrderId}
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                                                            {transaction.orderType || ''}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-gray-900">
                                                            <div>{transaction.consumerName || ''}</div>
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-gray-900">
                                                            <div>{transaction.cn || ''}</div>
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-gray-900">
                                                            <div>{transaction.txn || ''}</div>
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-semibold">
                                                            {formatCurrency(transaction.amount)}
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-mono font-semibold">
                                                            {formatCurrency(previousBalance)}
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                                                            {isCredit ? (
                                                                <span className="text-green-600 font-semibold">
                                                                    {formatCurrency(transaction.credit)}
                                                                </span>
                                                            ) : (
                                                                <span className="text-gray-300">-</span>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                                                            {isDebit ? (
                                                                <span className="text-red-600 font-semibold">
                                                                    {formatCurrency(transaction.debit)}
                                                                </span>
                                                            ) : (
                                                                <span className="text-gray-300">-</span>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-mono font-semibold">
                                                            {formatCurrency(balance)}
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap">
                                                            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(transaction.statusId)}`}>
                                                                {transaction.statusId}
                                                            </span>
                                                            {transaction.providerUniqueId && (
                                                                <div className="text-xs text-gray-400 mt-0.5">
                                                                    UID: {transaction.providerUniqueId}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                                            {transaction.operatorName || ''}
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                                            {transaction.providerName || ''}
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                                            <div className="text-xs text-gray-400">
                                                                {transaction.refundTime || "-"}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="17" className="px-4 py-8 text-center text-gray-500">
                                                    <div className="flex flex-col items-center">
                                                        <svg className="w-12 h-12 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2-2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                                                        </svg>
                                                        <p className="text-sm">No transactions found</p>
                                                        <p className="text-xs text-gray-400 mt-1">No ledger entries available for the selected filters</p>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* Transaction Summary */}
                    {filteredTransactions.length > 0 && (
                        <div className="mt-4 text-sm text-gray-500">
                            Showing {filteredTransactions.length} transaction{filteredTransactions.length > 1 ? 's' : ''}
                        </div>
                    )}
                </div>
            </div>

            <Toaster position="top-right" />
        </div>
    );
}

export default TransactionLedger;